/**
 * Provides an administration interface for managing role display names
 * throughout the application. Allows administrators to customize how
 * system roles are presented to users without modifying the underlying
 * role codes, while supporting change tracking, save operations, and
 * restoration of unsaved updates.
 */
import React, { useEffect, useState } from "react";

import { getRoleNames, updateRoleNames } from "@/services/commonconfig";

const RoleNameConfig: React.FC = () => {
  const [roles, setRoles] = useState<any[]>([]);
  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const data = await getRoleNames();

    setRoles(data);
    setInitial(JSON.stringify(data));
  };

  const hasChanges = initial !== "" && JSON.stringify(roles) !== initial;

  const handleSave = async () => {
    setSaving(true);

    await updateRoleNames(roles);

    setInitial(JSON.stringify(roles));

    setSaving(false);
  };

  const handleCancel = () => {
    setRoles(JSON.parse(initial));
  };

  const updateDisplayName = (roleId: number, value: string) => {
    setRoles((prev) =>
      prev.map((r) => (r.role_id === roleId ? { ...r, role_display_name: value } : r))
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-4">
      <div>
        <p className="text-xs text-gray-600">
          Configure the role names displayed throughout the application.
        </p>
      </div>

      {roles.map((role) => (
        <div key={role.role_id} className="flex items-center gap-4 text-xs">
          <span className="w-40 text-gray-700 font-medium">{role.role_code}</span>

          <input
            type="text"
            value={role.role_display_name ?? ""}
            onChange={(e) => updateDisplayName(role.role_id, e.target.value)}
            className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
            placeholder="Enter display name"
          />
        </div>
      ))}

      <div className="flex justify-end gap-2 items-center">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 rounded text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 rounded text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default RoleNameConfig;
